import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { inr, downloadFile, COMPANY_STYLES } from "@/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileDown } from "lucide-react";

export default function Money() {
  const [bills, setBills] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [company, setCompany] = useState("all");
  const [month, setMonth] = useState("all");
  const fyDefault = `${new Date().getFullYear()}-${String(new Date().getFullYear() + 1).slice(2)}`;
  const [fy, setFy] = useState(fyDefault);

  useEffect(() => {
    api.get("/bills").then((r) => setBills(r.data));
    api.get("/companies").then((r) => setCompanies(r.data));
  }, []);

  const months = useMemo(() => [...new Set(bills.map((b) => b.created_at.slice(0, 7)))].sort().reverse(), [bills]);
  const filtered = bills.filter((b) => (company === "all" || b.company === company) && (month === "all" || b.created_at.startsWith(month)));
  const sum = (k) => filtered.reduce((s, b) => s + (b[k] || 0), 0);
  const outstanding = filtered.filter((b) => b.payment_status !== "Full").reduce((s, b) => s + b.total_with_gst - (b.paid_amount || 0), 0);

  const byMonth = useMemo(() => {
    const m = {};
    for (const b of filtered) {
      const k = b.created_at.slice(0, 7);
      const d = (m[k] = m[k] || { bills: 0, revenue: 0, gst: 0, commission: 0, net: 0 });
      d.bills++; d.revenue += b.bill_amount; d.gst += b.gst_amount; d.commission += b.commission_amount; d.net += b.net_earning;
    }
    return Object.entries(m).sort((a, b) => b[0].localeCompare(a[0]));
  }, [filtered]);

  const Stat = ({ label, value, testid, accent }) => (
    <Card className="p-5" data-testid={testid}>
      <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="font-mono text-2xl font-bold mt-2" style={accent ? { color: accent } : {}}>{value}</div>
    </Card>
  );

  return (
    <div className="space-y-5" data-testid="money-page">
      <div className="rounded-[28px] border border-border/70 bg-surface-elevated p-5 sm:p-6">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.22em] text-primary">Master view</p>
            <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">All Money — Master View</h1>
            <p className="mt-1 text-sm text-muted-foreground">Combined financials across all 5 brands · Super Admin only</p>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <Input type="text" value={fy} onChange={(e) => setFy(e.target.value)} className="h-10 w-28 font-mono" data-testid="fy-input" title="FY e.g. 2026-27" />
            <Button variant="outline" data-testid="fy-report-button" className="h-10 rounded-xl border-border/80 bg-background/70 hover:bg-accent"
              onClick={() => downloadFile(`/reports/fy.pdf?fy=${fy}`, `cleanmates_FY${fy}.pdf`).then(() => toast.success("FY report downloaded")).catch(() => toast.error("Failed"))}>
              <FileDown size={15} className="mr-2" /> FY Report PDF
            </Button>
          </div>
        </div>
      </div>

      <Card className="p-3 sm:p-4">
        <div className="flex gap-2 flex-wrap">
          <Select value={company} onValueChange={setCompany}>
            <SelectTrigger className="w-44 h-10" data-testid="money-company-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Companies</SelectItem>
              {companies.map((c) => <SelectItem key={c.code} value={c.name}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="w-40 h-10" data-testid="money-month-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Months</SelectItem>
              {months.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <Stat testid="money-revenue" label="Revenue" value={inr(sum("bill_amount"))} />
        <Stat testid="money-gst" label="GST Collected" value={inr(sum("gst_amount"))} />
        <Stat testid="money-commission" label="Commission Paid" value={inr(sum("commission_amount"))} />
        <Stat testid="money-travel" label="Travel Allowance" value={inr(sum("travel_allowance"))} />
        <Stat testid="money-net" label="Net Earning" value={inr(sum("net_earning"))} accent="hsl(var(--success))" />
        <Stat testid="money-outstanding" label="Outstanding" value={inr(outstanding)} accent="hsl(var(--danger))" />
      </div>

      <Card className="p-5">
        <h3 className="font-heading font-semibold mb-4">Per-Brand Breakdown</h3>
        <div className="grid md:grid-cols-2 xl:grid-cols-5 gap-3">
          {companies.map((c) => {
            const cb = filtered.filter((b) => b.company === c.name);
            const rev = cb.reduce((s, b) => s + b.bill_amount, 0);
            return (
              <div key={c.code} className="border border-border/70 rounded-xl bg-muted/30 p-4 transition-shadow hover:shadow-soft" data-testid={`money-brand-${c.code}`}>
                <span className={`text-xs px-2 py-1 rounded-full border ${COMPANY_STYLES[c.name] || ""}`}>{c.name}</span>
                <div className="font-mono text-xl font-bold mt-3">{inr(rev)}</div>
                <div className="text-xs text-muted-foreground mt-1">{cb.length} invoices · GST {inr(cb.reduce((s, b) => s + b.gst_amount, 0))}</div>
              </div>
            );
          })}
        </div>
      </Card>

      <Card className="overflow-hidden">
        <table className="w-full text-sm" data-testid="money-month-table">
          <thead className="bg-muted/60">
            <tr className="text-left text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              <th className="px-4 py-3">Month</th><th className="px-4 py-3 text-right">Invoices</th>
              <th className="px-4 py-3 text-right">Revenue</th><th className="px-4 py-3 text-right">GST</th>
              <th className="px-4 py-3 text-right">Commission</th><th className="px-4 py-3 text-right">Net</th>
            </tr>
          </thead>
          <tbody>
            {byMonth.map(([m, d]) => (
              <tr key={m} className="border-t border-border/70 transition-colors hover:bg-accent/40">
                <td className="px-4 py-3 font-mono">{m}</td>
                <td className="px-4 py-3 text-right font-mono">{d.bills}</td>
                <td className="px-4 py-3 text-right font-mono">{inr(d.revenue)}</td>
                <td className="px-4 py-3 text-right font-mono">{inr(d.gst)}</td>
                <td className="px-4 py-3 text-right font-mono">{inr(d.commission)}</td>
                <td className="px-4 py-3 text-right font-mono font-semibold text-success">{inr(d.net)}</td>
              </tr>
            ))}
            {!byMonth.length && <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">No invoices in this filter</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
